<script setup>
import { ref, computed, onMounted, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import scheduledMessagesAPI from 'dashboard/api/scheduledMessages';
import recurringMessagesAPI from 'dashboard/api/recurringMessages';
import { useAlert } from 'dashboard/composables';

const props = defineProps({
  conversationId: {
    type: [Number, String],
    required: true,
  },
  messageContent: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['close', 'scheduled']);

const { t } = useI18n();

const isRecurring = ref(false);
const scheduledDate = ref('');
const scheduledTime = ref('');
const content = ref(props.messageContent);
const isScheduling = ref(false);
const attachedFiles = ref([]);
const fileInputRef = ref(null);

const recurrenceType = ref('daily');
const recurrenceInterval = ref(1);
const recurrenceConfig = ref({
  hour: 9,
  minute: 0,
  days: [1, 2, 3, 4, 5],
  day: 1,
});
const endDate = ref('');
const maxExecutions = ref(null);

const weekDays = [
  { value: 0, label: 'RECURRING_MESSAGES.DAYS.SUNDAY' },
  { value: 1, label: 'RECURRING_MESSAGES.DAYS.MONDAY' },
  { value: 2, label: 'RECURRING_MESSAGES.DAYS.TUESDAY' },
  { value: 3, label: 'RECURRING_MESSAGES.DAYS.WEDNESDAY' },
  { value: 4, label: 'RECURRING_MESSAGES.DAYS.THURSDAY' },
  { value: 5, label: 'RECURRING_MESSAGES.DAYS.FRIDAY' },
  { value: 6, label: 'RECURRING_MESSAGES.DAYS.SATURDAY' },
];

const isValid = computed(() => {
  const hasContent =
    content.value.trim().length > 0 || attachedFiles.value.length > 0;
  const hasDateTime = scheduledDate.value && scheduledTime.value;

  if (!hasContent || !hasDateTime) return false;

  if (isRecurring.value) {
    if (
      recurrenceType.value === 'weekly' &&
      (!recurrenceConfig.value.days || recurrenceConfig.value.days.length === 0)
    ) {
      return false;
    }
  }

  return true;
});

const getMinDateTime = () => {
  const now = new Date();
  now.setMinutes(now.getMinutes() + 5);
  return now;
};

const getMinDate = () => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getMinTime = () => {
  if (scheduledDate.value === getMinDate()) {
    const min = getMinDateTime();
    return `${String(min.getHours()).padStart(2, '0')}:${String(min.getMinutes()).padStart(2, '0')}`;
  }
  return '00:00';
};

onMounted(() => {
  const min = getMinDateTime();
  const year = min.getFullYear();
  const month = String(min.getMonth() + 1).padStart(2, '0');
  const day = String(min.getDate()).padStart(2, '0');
  scheduledDate.value = `${year}-${month}-${day}`;
  scheduledTime.value = `${String(min.getHours()).padStart(2, '0')}:${String(min.getMinutes()).padStart(2, '0')}`;
});

const onClose = () => {
  emit('close');
};

const handleFileSelect = event => {
  const files = Array.from(event.target.files);
  if (attachedFiles.value.length + files.length > 15) {
    useAlert(t('SCHEDULED_MESSAGES.MAX_FILES_ERROR'));
    return;
  }
  attachedFiles.value = [...attachedFiles.value, ...files];
};

const removeFile = index => {
  attachedFiles.value.splice(index, 1);
};

const triggerFileInput = () => {
  fileInputRef.value?.click();
};

const formatFileSize = bytes => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${Math.round((bytes / k ** i) * 100) / 100} ${sizes[i]}`;
};

const toggleWeekDay = day => {
  const days = recurrenceConfig.value.days || [];
  const index = days.indexOf(day);
  if (index > -1) {
    days.splice(index, 1);
  } else {
    days.push(day);
  }
  recurrenceConfig.value.days = days.sort((a, b) => a - b);
};

const isWeekDaySelected = day => {
  return (recurrenceConfig.value.days || []).includes(day);
};

const scheduleMessage = async () => {
  if (!isValid.value) return;

  isScheduling.value = true;
  try {
    const dateTimeString = `${scheduledDate.value}T${scheduledTime.value}:00`;
    const startDate = new Date(dateTimeString).toISOString();

    const formData = new FormData();

    if (isRecurring.value) {
      formData.append('recurring_message[content]', content.value || '');
      formData.append('recurring_message[content_type]', 'text');
      formData.append(
        'recurring_message[recurrence_type]',
        recurrenceType.value
      );
      formData.append(
        'recurring_message[recurrence_interval]',
        recurrenceInterval.value
      );
      formData.append('recurring_message[start_date]', startDate);

      const config = {};
      if (recurrenceType.value === 'hourly') {
        config.minute = recurrenceConfig.value.minute || 0;
      } else if (recurrenceType.value === 'daily') {
        config.hour = recurrenceConfig.value.hour || 9;
        config.minute = recurrenceConfig.value.minute || 0;
      } else if (recurrenceType.value === 'weekly') {
        config.days = recurrenceConfig.value.days || [1];
        config.hour = recurrenceConfig.value.hour || 9;
        config.minute = recurrenceConfig.value.minute || 0;
      } else if (recurrenceType.value === 'monthly') {
        config.day = recurrenceConfig.value.day || 1;
        config.hour = recurrenceConfig.value.hour || 9;
        config.minute = recurrenceConfig.value.minute || 0;
      }

      formData.append(
        'recurring_message[recurrence_config]',
        JSON.stringify(config)
      );

      if (endDate.value) {
        const endDateTime = new Date(`${endDate.value}T23:59:59`).toISOString();
        formData.append('recurring_message[end_date]', endDateTime);
      }

      if (maxExecutions.value) {
        formData.append(
          'recurring_message[max_executions]',
          maxExecutions.value
        );
      }

      attachedFiles.value.forEach(file => {
        formData.append('attachments[]', file);
      });

      await recurringMessagesAPI.create(props.conversationId, formData);
      useAlert(t('RECURRING_MESSAGES.SCHEDULE_SUCCESS'));
    } else {
      formData.append('scheduled_message[content]', content.value || '');
      formData.append('scheduled_message[scheduled_at]', startDate);
      formData.append('scheduled_message[content_type]', 'text');

      attachedFiles.value.forEach(file => {
        formData.append('attachments[]', file);
      });

      await scheduledMessagesAPI.create(props.conversationId, formData);
      useAlert(t('SCHEDULED_MESSAGES.SCHEDULE_SUCCESS'));
    }

    await nextTick();
    emit('scheduled');
    emit('close');
  } catch (error) {
    useAlert(t('SCHEDULED_MESSAGES.SCHEDULE_ERROR'));
  } finally {
    isScheduling.value = false;
  }
};
</script>

<template>
  <div class="flex flex-col">
    <woot-modal-header
      :header-title="$t('SCHEDULED_MESSAGES.TITLE')"
      :header-content="$t('SCHEDULED_MESSAGES.DESCRIPTION')"
    />
    <form
      class="modal-content w-full pt-2 px-5 pb-6 max-h-[70vh] overflow-y-auto"
      @submit.prevent="scheduleMessage"
    >
      <div class="mb-4">
        <label class="flex items-center gap-2 cursor-pointer">
          <input
            v-model="isRecurring"
            type="checkbox"
            class="w-4 h-4 text-woot-500 border-slate-300 rounded focus:ring-woot-500"
          />
          <span class="text-sm font-medium text-slate-700 dark:text-slate-200">
            {{ $t('RECURRING_MESSAGES.ENABLE_RECURRING') }}
          </span>
        </label>
      </div>

      <div class="mb-4">
        <label
          class="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-2"
        >
          {{ $t('SCHEDULED_MESSAGES.MESSAGE_LABEL') }}
        </label>
        <textarea
          v-model="content"
          class="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md focus:outline-none focus:ring-2 focus:ring-woot-500 dark:bg-slate-800 dark:text-slate-100"
          rows="4"
          :placeholder="$t('SCHEDULED_MESSAGES.MESSAGE_PLACEHOLDER')"
        />
      </div>

      <div class="mb-4">
        <label
          class="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-2"
        >
          {{ $t('SCHEDULED_MESSAGES.ATTACHMENTS_LABEL') }}
        </label>
        <input
          ref="fileInputRef"
          type="file"
          multiple
          class="hidden"
          accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx,.txt"
          @change="handleFileSelect"
        />
        <button
          type="button"
          class="flex items-center gap-2 px-4 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-md hover:bg-slate-50 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200"
          @click="triggerFileInput"
        >
          <Icon icon="i-material-symbols-attach-file" />
          {{ $t('SCHEDULED_MESSAGES.ADD_ATTACHMENT') }}
        </button>
        <div v-if="attachedFiles.length > 0" class="mt-3 space-y-2">
          <div
            v-for="(file, index) in attachedFiles"
            :key="index"
            class="flex items-center justify-between p-2 bg-slate-50 dark:bg-slate-700 rounded-md"
          >
            <div class="flex items-center gap-2 flex-1 min-w-0">
              <Icon
                icon="i-material-symbols-description"
                class="text-slate-500 dark:text-slate-400"
              />
              <div class="flex-1 min-w-0">
                <p class="text-sm text-slate-700 dark:text-slate-200 truncate">
                  {{ file.name }}
                </p>
                <p class="text-xs text-slate-500 dark:text-slate-400">
                  {{ formatFileSize(file.size) }}
                </p>
              </div>
            </div>
            <button
              type="button"
              class="text-red-500 hover:text-red-700 ml-2"
              @click="removeFile(index)"
            >
              <Icon icon="i-material-symbols-close" />
            </button>
          </div>
        </div>
        <p class="text-xs text-slate-500 dark:text-slate-400 mt-2">
          {{ $t('SCHEDULED_MESSAGES.MAX_FILES_INFO') }}
        </p>
      </div>

      <div
        v-if="isRecurring"
        class="mb-4 p-4 border border-slate-300 dark:border-slate-600 rounded-md bg-slate-50 dark:bg-slate-800"
      >
        <h3 class="text-sm font-medium text-slate-700 dark:text-slate-200 mb-3">
          {{ $t('RECURRING_MESSAGES.RECURRENCE_SETTINGS') }}
        </h3>

        <div class="mb-3">
          <label
            class="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-2"
          >
            {{ $t('RECURRING_MESSAGES.RECURRENCE_TYPE') }}
          </label>
          <select
            v-model="recurrenceType"
            class="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md focus:outline-none focus:ring-2 focus:ring-woot-500 dark:bg-slate-700 dark:text-slate-100"
          >
            <option value="hourly">
              {{ $t('RECURRING_MESSAGES.TYPES.HOURLY') }}
            </option>
            <option value="daily">
              {{ $t('RECURRING_MESSAGES.TYPES.DAILY') }}
            </option>
            <option value="weekly">
              {{ $t('RECURRING_MESSAGES.TYPES.WEEKLY') }}
            </option>
            <option value="monthly">
              {{ $t('RECURRING_MESSAGES.TYPES.MONTHLY') }}
            </option>
          </select>
        </div>

        <div class="mb-3">
          <label
            class="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-2"
          >
            {{ $t('RECURRING_MESSAGES.INTERVAL') }}
          </label>
          <input
            v-model.number="recurrenceInterval"
            type="number"
            min="1"
            class="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md focus:outline-none focus:ring-2 focus:ring-woot-500 dark:bg-slate-700 dark:text-slate-100"
          />
          <p class="text-xs text-slate-500 dark:text-slate-400 mt-1">
            {{
              $t(
                `RECURRING_MESSAGES.INTERVAL_HINT.${recurrenceType.toUpperCase()}`
              )
            }}
          </p>
        </div>

        <div v-if="recurrenceType === 'hourly'" class="mb-3">
          <label
            class="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-2"
          >
            {{ $t('RECURRING_MESSAGES.MINUTE_OF_HOUR') }}
          </label>
          <input
            v-model.number="recurrenceConfig.minute"
            type="number"
            min="0"
            max="59"
            class="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md focus:outline-none focus:ring-2 focus:ring-woot-500 dark:bg-slate-700 dark:text-slate-100"
          />
        </div>

        <div v-if="recurrenceType === 'daily'" class="mb-3">
          <label
            class="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-2"
          >
            {{ $t('RECURRING_MESSAGES.TIME_OF_DAY') }}
          </label>
          <div class="grid grid-cols-2 gap-2">
            <div>
              <input
                v-model.number="recurrenceConfig.hour"
                type="number"
                min="0"
                max="23"
                placeholder="Hora"
                class="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md focus:outline-none focus:ring-2 focus:ring-woot-500 dark:bg-slate-700 dark:text-slate-100"
              />
            </div>
            <div>
              <input
                v-model.number="recurrenceConfig.minute"
                type="number"
                min="0"
                max="59"
                placeholder="Minuto"
                class="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md focus:outline-none focus:ring-2 focus:ring-woot-500 dark:bg-slate-700 dark:text-slate-100"
              />
            </div>
          </div>
        </div>

        <div v-if="recurrenceType === 'weekly'" class="mb-3">
          <label
            class="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-2"
          >
            {{ $t('RECURRING_MESSAGES.DAYS_OF_WEEK') }}
          </label>
          <div class="flex flex-wrap gap-2 mb-3">
            <button
              v-for="day in weekDays"
              :key="day.value"
              type="button"
              class="px-3 py-1 text-sm rounded-md border transition-colors"
              :class="[
                isWeekDaySelected(day.value)
                  ? 'bg-woot-500 text-white border-woot-500'
                  : 'bg-white dark:bg-slate-700 text-slate-700 dark:text-slate-200 border-slate-300 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-600',
              ]"
              @click="toggleWeekDay(day.value)"
            >
              {{ $t(day.label) }}
            </button>
          </div>
          <label
            class="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-2"
          >
            {{ $t('RECURRING_MESSAGES.TIME_OF_DAY') }}
          </label>
          <div class="grid grid-cols-2 gap-2">
            <div>
              <input
                v-model.number="recurrenceConfig.hour"
                type="number"
                min="0"
                max="23"
                placeholder="Hora"
                class="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md focus:outline-none focus:ring-2 focus:ring-woot-500 dark:bg-slate-700 dark:text-slate-100"
              />
            </div>
            <div>
              <input
                v-model.number="recurrenceConfig.minute"
                type="number"
                min="0"
                max="59"
                placeholder="Minuto"
                class="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md focus:outline-none focus:ring-2 focus:ring-woot-500 dark:bg-slate-700 dark:text-slate-100"
              />
            </div>
          </div>
        </div>

        <div v-if="recurrenceType === 'monthly'" class="mb-3">
          <label
            class="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-2"
          >
            {{ $t('RECURRING_MESSAGES.DAY_OF_MONTH') }}
          </label>
          <input
            v-model.number="recurrenceConfig.day"
            type="number"
            min="1"
            max="31"
            class="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md focus:outline-none focus:ring-2 focus:ring-woot-500 dark:bg-slate-700 dark:text-slate-100"
          />
          <label
            class="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-2 mt-3"
          >
            {{ $t('RECURRING_MESSAGES.TIME_OF_DAY') }}
          </label>
          <div class="grid grid-cols-2 gap-2">
            <div>
              <input
                v-model.number="recurrenceConfig.hour"
                type="number"
                min="0"
                max="23"
                placeholder="Hora"
                class="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md focus:outline-none focus:ring-2 focus:ring-woot-500 dark:bg-slate-700 dark:text-slate-100"
              />
            </div>
            <div>
              <input
                v-model.number="recurrenceConfig.minute"
                type="number"
                min="0"
                max="59"
                placeholder="Minuto"
                class="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md focus:outline-none focus:ring-2 focus:ring-woot-500 dark:bg-slate-700 dark:text-slate-100"
              />
            </div>
          </div>
        </div>

        <div class="mb-3">
          <label
            class="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-2"
          >
            {{ $t('RECURRING_MESSAGES.END_DATE') }} ({{
              $t('RECURRING_MESSAGES.OPTIONAL')
            }})
          </label>
          <input
            v-model="endDate"
            type="date"
            :min="scheduledDate"
            class="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md focus:outline-none focus:ring-2 focus:ring-woot-500 dark:bg-slate-700 dark:text-slate-100"
          />
        </div>

        <div class="mb-3">
          <label
            class="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-2"
          >
            {{ $t('RECURRING_MESSAGES.MAX_EXECUTIONS') }} ({{
              $t('RECURRING_MESSAGES.OPTIONAL')
            }})
          </label>
          <input
            v-model.number="maxExecutions"
            type="number"
            min="1"
            :placeholder="$t('RECURRING_MESSAGES.MAX_EXECUTIONS_PLACEHOLDER')"
            class="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md focus:outline-none focus:ring-2 focus:ring-woot-500 dark:bg-slate-700 dark:text-slate-100"
          />
        </div>
      </div>

      <div class="mb-4">
        <label
          class="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-2"
        >
          {{
            isRecurring
              ? $t('RECURRING_MESSAGES.START_DATE')
              : $t('SCHEDULED_MESSAGES.DATE_TIME_LABEL')
          }}
        </label>
        <div class="grid grid-cols-2 gap-3">
          <div>
            <input
              v-model="scheduledDate"
              type="date"
              :min="getMinDate()"
              class="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md focus:outline-none focus:ring-2 focus:ring-woot-500 dark:bg-slate-800 dark:text-slate-100"
            />
          </div>
          <div>
            <input
              v-model="scheduledTime"
              type="time"
              :min="getMinTime()"
              class="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md focus:outline-none focus:ring-2 focus:ring-woot-500 dark:bg-slate-800 dark:text-slate-100"
            />
          </div>
        </div>
      </div>

      <div class="flex flex-row justify-end w-full gap-2 px-0 py-2 mt-4">
        <NextButton
          faded
          slate
          type="button"
          :label="$t('SCHEDULED_MESSAGES.CANCEL')"
          @click="onClose"
        />
        <NextButton
          type="submit"
          :label="
            isRecurring
              ? $t('RECURRING_MESSAGES.CREATE_BUTTON')
              : $t('SCHEDULED_MESSAGES.SCHEDULE_BUTTON')
          "
          :disabled="!isValid || isScheduling"
          :loading="isScheduling"
        />
      </div>
    </form>
  </div>
</template>
